import { useMemo } from "react";
import { Structure, StructureType } from "./api";

/**
 * Hook to get the structure type configuration for a structure.
 * Returns the config with all features and settings.
 */
export function useStructureTypeConfig(structure: Structure | null, structureTypes: StructureType[] = []) {
  return useMemo(() => {
    if (!structure || !structure.structure_type_key) {
      // Default config for organigramm
      return {
        defaultNodeTypeKey: "group",
        availableLayouts: ["rectangle"],
        features: {
          contacts: true,
          links: true,
          notes: true,
          customFields: false,
          decisionBranching: false,
          swimlanes: false,
        },
        nodeFieldsSchema: {},
        edgeTypes: ["default"],
        explorerDefaults: {
          showContactsExplorer: true,
          showLinksPanel: true,
        },
      };
    }

    const structureType = structureTypes.find((t) => t.key === structure.structure_type_key);
    if (!structureType || !structureType.config) {
      // Fallback to default
      return {
        defaultNodeTypeKey: "group",
        availableLayouts: ["rectangle"],
        features: {
          contacts: true,
          links: true,
          notes: true,
          customFields: false,
          decisionBranching: false,
          swimlanes: false,
        },
        nodeFieldsSchema: {},
        edgeTypes: ["default"],
        explorerDefaults: {
          showContactsExplorer: true,
          showLinksPanel: true,
        },
      };
    }

    return structureType.config;
  }, [structure, structureTypes]);
}

/**
 * Helper hook to check if a specific feature is enabled for a structure.
 */
export function useStructureFeature(
  structure: Structure | null,
  structureTypes: StructureType[] = [],
  feature: keyof NonNullable<StructureType["config"]["features"]>
): boolean {
  const config = useStructureTypeConfig(structure, structureTypes);
  return config.features?.[feature] ?? false;
}
